/** @copyright (c) Viewpost. All Rights Reserved. See LICENSE for more details. */

import React, { useState } from 'react';

import LoadingIndicator from 'components/LoadingIndicator/Base';

import { CallbackOpener, openCallbackWindow } from 'views/Onboarding/Callback';
import Button from 'components/Button';

import useApi from 'api/hooks/useApi';
import { createPushToCardUrl, refreshPushToCardAccounts } from 'api/payments/pushToCard';

function modifyUrl(url) {
  if (!url) return url;

  // the redirect url is really just to get to our callback page
  // but the configured domain via dash may not align with the actual domain we are in now
  // so rewrite the url dynamically
  const u = new URL(url);
  u.searchParams.set('return_url', `${window.location.origin}/callback`);
  return u.toString();
}

function AddCard({
  addressBookId,
  onComplete
}) {
  const [error, setError] = useState();

  const urlApi = useApi(createPushToCardUrl, 'CreatePushToCardUrl', {
    onComplete: (success) => {
      if (!success) {
        setError('Unable to load account information.');
      }
    }
  });
  const refreshApi = useApi(refreshPushToCardAccounts, 'RefreshPushToCard', {
    onComplete: (success, action) => {
      if (action?.payload?.response?.body?.cards?.length) {
        // at least 1 card has been added
        onComplete?.();
      } else {
        // no card has been added, user needs to retry
        setError('No Card Accounts were added.');
      }
    }
  });

  const loadUrl = () => {
    if (addressBookId) {
      urlApi.call({
        addressBookId
      });
      setError(null);
    }
  };

  const openTabAndLoadUrl = () => {
    // Required for Safari (iOS)
    openCallbackWindow();

    loadUrl();
  };

  const url = modifyUrl(urlApi.getResponse()?.url);

  const complete = () => {
    refreshApi.call({
      addressBookId
    });
  };

  if (urlApi.isLoading()) {
    return (
      <LoadingIndicator>
        Pulling up account information...
      </LoadingIndicator>
    );
  }

  if (refreshApi.isLoading()) {
    return (
      <LoadingIndicator>
        Updating your card account...
      </LoadingIndicator>
    );
  }

  // initial state AND error state for retry
  // user must press button to kick it off
  //     to get past browser popup blocks
  if (!url || error) {
    return (
      <div>
        {error
          ? (
            <div style={{
              fontSize: '18px',
              paddingBottom: '20px'
            }}>
              <div className="vp-error">
                {error}
              </div>
            </div>
          )
          : null}
        <Button onClick={() => openTabAndLoadUrl()}>
          {error ? 'Retry' : 'Open New Tab to Add Card'}
        </Button>
      </div>
    );
  }

  return (
    <CallbackOpener url={url} onComplete={() => complete()}>
      <LoadingIndicator style={{ paddingBottom: '50px' }}>
        Waiting for completion of card account information
        in another window...
        <br /><br />
        <a onClick={() => openTabAndLoadUrl()}>
          Retry
        </a>
      </LoadingIndicator>
    </CallbackOpener>
  );
}

export default AddCard;

